import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/store/authStore'
import { getApiBaseUrl } from '@/config/env'

export interface ChatEventPayload {
  type: string
  tenantId: string
  conversationId: string
  messageId: string
  timestamp: string
  data: any
}

export interface LegacyMessageAckPayload {
  messageId: string
  conversationId: string
  ack: number
  status: string
  ackTimestamp: string
}

export interface TypingEventPayload {
  conversationId: string
  userId: string
  userName?: string
  isTyping: boolean
}

export interface ConversationUpdatedPayload {
  conversationId: string
  mode?: 'bot' | 'human'
  status?: string
  assignedTeamId?: string | null
  assignedUserId?: string | null
  reason?: string
  customerName?: string | null
  customerPhone?: string | null
  escalated?: boolean
  escalatedBy?: 'ai_agent' | 'human'
  summary?: string | null
}

export interface ConversationEscalatedPayload {
  tenantId?: string
  conversationId: string
  customerName?: string | null
  customerPhone?: string | null
  reason?: string
  summary?: string | null
  assignedTeamId?: string | null
  assignedUserId?: string | null
  escalatedBy?: 'ai_agent' | 'human'
  timestamp?: string
}

type ChatEventListener = (event: ChatEventPayload) => void
type MessageAckListener = (event: LegacyMessageAckPayload) => void
type TypingListener = (event: TypingEventPayload) => void
type StatusListener = (connected: boolean) => void
type ReconnectListener = () => void
type ConversationUpdatedListener = (event: ConversationUpdatedPayload) => void
type ConversationEscalatedListener = (event: ConversationEscalatedPayload) => void

class ConversationSocketManager {
  private socket: Socket | null = null
  private subscriberCount = 0
  private isConnected = false
  private chatEventListeners = new Set<ChatEventListener>()
  private messageAckListeners = new Set<MessageAckListener>()
  private typingListeners = new Set<TypingListener>()
  private statusListeners = new Set<StatusListener>()
  private reconnectListeners = new Set<ReconnectListener>()
  private conversationUpdatedListeners = new Set<ConversationUpdatedListener>()
  private conversationEscalatedListeners = new Set<ConversationEscalatedListener>()

  public getSocket(): Socket | null {
    return this.socket
  }

  public getIsConnected(): boolean {
    return this.isConnected
  }

  public subscribe(options?: {
    onChatEvent?: ChatEventListener
    onMessageAck?: MessageAckListener
    onTyping?: TypingListener
    onStatusChange?: StatusListener
    onReconnect?: ReconnectListener
    onConversationUpdated?: ConversationUpdatedListener
    onConversationEscalated?: ConversationEscalatedListener
  }): () => void {
    if (options?.onChatEvent) this.chatEventListeners.add(options.onChatEvent)
    if (options?.onMessageAck) this.messageAckListeners.add(options.onMessageAck)
    if (options?.onTyping) this.typingListeners.add(options.onTyping)
    if (options?.onConversationUpdated) this.conversationUpdatedListeners.add(options.onConversationUpdated)
    if (options?.onConversationEscalated) this.conversationEscalatedListeners.add(options.onConversationEscalated)
    if (options?.onStatusChange) {
      this.statusListeners.add(options.onStatusChange)
      options.onStatusChange(this.isConnected)
    }
    if (options?.onReconnect) this.reconnectListeners.add(options.onReconnect)

    this.subscriberCount++
    if (this.subscriberCount === 1) {
      this.initSocket()
    }

    return () => {
      if (options?.onChatEvent) this.chatEventListeners.delete(options.onChatEvent)
      if (options?.onMessageAck) this.messageAckListeners.delete(options.onMessageAck)
      if (options?.onTyping) this.typingListeners.delete(options.onTyping)
      if (options?.onConversationUpdated) this.conversationUpdatedListeners.delete(options.onConversationUpdated)
      if (options?.onConversationEscalated) this.conversationEscalatedListeners.delete(options.onConversationEscalated)
      if (options?.onStatusChange) this.statusListeners.delete(options.onStatusChange)
      if (options?.onReconnect) this.reconnectListeners.delete(options.onReconnect)

      this.subscriberCount--
      if (this.subscriberCount <= 0) {
        this.subscriberCount = 0
        this.destroySocket()
      }
    }
  }

  public emitTyping(conversationId: string, isTyping: boolean) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('chat:typing', {
        conversationId,
        isTyping,
      })
    }
  }

  private initSocket() {
    const { token } = useAuthStore.getState()
    if (!token) return

    const API_BASE_URL = getApiBaseUrl()

    this.socket = io(`${API_BASE_URL}/admin/monitoring`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
    })

    this.socket.on('connect', () => {
      this.isConnected = true
      this.notifyStatus(true)
    })

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false
      this.notifyStatus(false)

      if (reason === 'io server disconnect' || reason === 'transport close') {
        setTimeout(() => {
          const { token: freshToken } = useAuthStore.getState()
          if (freshToken && this.socket && this.socket.auth && typeof this.socket.auth === 'object') {
            ;(this.socket.auth as any).token = freshToken
            this.socket.connect()
          }
        }, 1000)
      }
    })

    this.socket.on('connect_error', (error) => {
      this.isConnected = false
      this.notifyStatus(false)

      if (error.message.includes('Unauthorized') || error.message.includes('token')) {
        const { token: freshToken } = useAuthStore.getState()
        if (freshToken && this.socket && this.socket.auth && typeof this.socket.auth === 'object') {
          ;(this.socket.auth as any).token = freshToken
        }
      }
    })

    this.socket.on('reconnect', () => {
      this.isConnected = true
      this.notifyStatus(true)
      this.reconnectListeners.forEach((listener) => {
        try {
          listener()
        } catch {}
      })
    })

    this.socket.on('chat:event', (event: ChatEventPayload) => {
      this.chatEventListeners.forEach((listener) => {
        try {
          listener(event)
        } catch {}
      })
    })

    this.socket.on('message:ack', (ackData: LegacyMessageAckPayload) => {
      this.messageAckListeners.forEach((listener) => {
        try {
          listener(ackData)
        } catch {}
      })
    })

    this.socket.on('chat:typing', (typingData: TypingEventPayload) => {
      this.typingListeners.forEach((listener) => {
        try {
          listener(typingData)
        } catch {}
      })
    })

    this.socket.on('conversation:updated', (convData: ConversationUpdatedPayload) => {
      this.conversationUpdatedListeners.forEach((listener) => {
        try {
          listener(convData)
        } catch {}
      })
    })

    this.socket.on('conversation:escalated', (escalatedData: ConversationEscalatedPayload) => {
      this.conversationEscalatedListeners.forEach((listener) => {
        try {
          listener(escalatedData)
        } catch {}
      })
    })
  }

  private notifyStatus(connected: boolean) {
    this.statusListeners.forEach((listener) => {
      try {
        listener(connected)
      } catch {}
    })
  }

  private destroySocket() {
    if (this.socket) {
      this.socket.off('connect')
      this.socket.off('disconnect')
      this.socket.off('connect_error')
      this.socket.off('reconnect')
      this.socket.off('chat:event')
      this.socket.off('message:ack')
      this.socket.off('chat:typing')
      this.socket.off('conversation:updated')
      this.socket.off('conversation:escalated')
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.notifyStatus(false)
    }
  }
}

export const conversationSocket = new ConversationSocketManager()
